import React, { useEffect, useState } from 'react';
import { Bell, RefreshCw, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import axios, { API_BASE_URL } from './apiConfig';

const DEFAULT_BANNER = {
  enabled: true,
  message: 'Revised prices will be effective from 1 November 2026.',
};

const SettingsSiteBanner = () => {
  const [banner, setBanner] = useState(DEFAULT_BANNER);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('admin_token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/api/admin/settings?id=global`, { headers });
        const savedBanner = data?.settings?.site_banner;
        if (savedBanner && typeof savedBanner === 'object') {
          setBanner({
            enabled: savedBanner.enabled !== false,
            message: typeof savedBanner.message === 'string'
              ? savedBanner.message
              : DEFAULT_BANNER.message,
          });
        }
      } catch (error) {
        console.error('Failed to load site banner settings', error);
        toast.error('Failed to load site banner settings');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveBanner = async () => {
    try {
      setSaving(true);
      const { data } = await axios.get(`${API_BASE_URL}/api/admin/settings?id=global`, { headers });
      await axios.put(`${API_BASE_URL}/api/admin/settings`, {
        ...(data.settings || {}),
        site_banner: {
          enabled: banner.enabled,
          message: banner.message.trim(),
        },
      }, { headers });
      toast.success('Site banner saved');
    } catch (error) {
      console.error('Failed to save site banner settings', error);
      toast.error('Failed to save site banner settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-4 text-gray-500">Loading site banner settings...</div>;

  return (
    <section className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-8 py-6 border-b border-gray-50 flex items-center gap-4 bg-gray-50/30">
        <div className="p-3 bg-[#471F21]/5 text-[#471F21] rounded-xl">
          <Bell className="w-6 h-6" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-800">Site Banner</h2>
          <p className="text-sm text-gray-500 mt-0.5">Show a message above every page on the website.</p>
        </div>
      </div>

      <div className="p-8 space-y-6">
        <label className="flex items-center justify-between gap-6 p-5 bg-gray-50 rounded-xl border border-gray-100 cursor-pointer">
          <span>
            <span className="block text-sm font-bold text-gray-800">Display banner</span>
            <span className="block text-xs text-gray-500 mt-1">Turn this off to hide the message across the site.</span>
          </span>
          <input
            type="checkbox"
            checked={banner.enabled}
            onChange={(event) => setBanner({ ...banner, enabled: event.target.checked })}
            className="h-5 w-5 accent-[#471F21]"
          />
        </label>

        <div>
          <label htmlFor="site-banner-message" className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">
            Banner message
          </label>
          <input
            id="site-banner-message"
            type="text"
            value={banner.message}
            onChange={(event) => setBanner({ ...banner, message: event.target.value })}
            maxLength={250}
            placeholder="Enter the message to show site-wide"
            className="w-full px-5 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 focus:border-[#471F21] focus:ring-0 outline-none transition-all shadow-sm"
          />
          <p className="text-xs text-gray-400 mt-2">A blank message will not be displayed.</p>
        </div>

        <div className="flex justify-end pt-2 border-t border-gray-50">
          <button
            type="button"
            onClick={saveBanner}
            disabled={saving}
            className="flex items-center gap-3 px-8 py-3 bg-[#471F21] text-white font-bold rounded-xl hover:shadow-xl hover:shadow-[#471F21]/20 disabled:opacity-50 transition-all"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Banner
          </button>
        </div>
      </div>
    </section>
  );
};

export default SettingsSiteBanner;
